import ms from 'ms'
import { animate } from 'framer-motion'
import type { MotionValue } from 'framer-motion'
import {
  GAZE_RANGE_PX,
  GAZE_RELEASE_PX,
  pointerEngaged,
  pointerX,
  pointerY,
} from './aida-icon-gaze'

type AttentionMode = 'static' | 'looking'

interface AttentionSession {
  stopped: boolean
  busy: boolean
  timer: number
  mode: AttentionMode
  blink: boolean
  lidScale: MotionValue<number>
  looking: MotionValue<number>
  centerX: MotionValue<number>
  centerY: MotionValue<number>
}

const BLINK_CLOSE_S = ms('70ms') / ms('1s')
const BLINK_OPEN_S = ms('110ms') / ms('1s')
const DOUBLE_BLINK_GAP_MS = ms('120ms')
const IDLE_BLINK_MIN_MS = ms('2.5s')
const IDLE_BLINK_SPREAD_MS = ms('3.5s')
const DOUBLE_BLINK_CHANCE = 0.15
const LID_CLOSED = 0.1
const LID_OPEN = 1

function readAttentionTarget(mode: AttentionMode, centerX: number, centerY: number): AttentionMode {
  if (pointerEngaged.get() === 0) {
    return 'static'
  }
  const dx = pointerX.get() - centerX
  const dy = pointerY.get() - centerY
  const distance = Math.hypot(dx, dy)
  const limit = mode === 'looking' ? GAZE_RELEASE_PX : GAZE_RANGE_PX
  return distance > 0 && distance <= limit ? 'looking' : 'static'
}

async function runLidBlink(lidScale: MotionValue<number>, stopped: () => boolean) {
  const closing = animate(lidScale, LID_CLOSED, { duration: BLINK_CLOSE_S, ease: 'easeIn' })
  await closing
  if (stopped()) {
    return
  }
  const opening = animate(lidScale, LID_OPEN, { duration: BLINK_OPEN_S, ease: 'easeOut' })
  await opening
}

function applyAttentionMode(looking: MotionValue<number>, mode: AttentionMode) {
  looking.set(mode === 'looking' ? 1 : 0)
}

function target(session: AttentionSession): AttentionMode {
  return readAttentionTarget(session.mode, session.centerX.get(), session.centerY.get())
}

function armIdle(session: AttentionSession) {
  window.clearTimeout(session.timer)
  if (!session.blink || session.mode !== 'looking') {
    return
  }
  session.timer = window.setTimeout(
    () => {
      void idleBlink(session)
    },
    IDLE_BLINK_MIN_MS + Math.random() * IDLE_BLINK_SPREAD_MS,
  )
}

async function idleBlink(session: AttentionSession) {
  if (session.stopped || session.busy || session.mode !== 'looking') {
    return
  }
  session.busy = true
  await runLidBlink(session.lidScale, () => session.stopped)
  session.busy = false
  if (session.stopped || session.mode !== 'looking') {
    return
  }
  if (Math.random() < DOUBLE_BLINK_CHANCE) {
    session.timer = window.setTimeout(() => {
      void idleBlink(session)
    }, DOUBLE_BLINK_GAP_MS)
    return
  }
  armIdle(session)
}

function finishTransition(session: AttentionSession) {
  session.busy = false
  if (session.stopped) {
    return
  }
  session.mode = target(session)
  applyAttentionMode(session.looking, session.mode)
  armIdle(session)
  sync(session)
}

function sync(session: AttentionSession) {
  const next = target(session)
  if (next === session.mode || session.busy) {
    return
  }
  window.clearTimeout(session.timer)
  if (!session.blink) {
    session.mode = next
    applyAttentionMode(session.looking, session.mode)
    return
  }
  session.busy = true
  if (session.mode === 'looking' && next === 'static') {
    applyAttentionMode(session.looking, 'static')
  }
  void runLidBlink(session.lidScale, () => session.stopped).then(() => finishTransition(session))
}

export function watchAttention(
  lidScale: MotionValue<number>,
  looking: MotionValue<number>,
  centerX: MotionValue<number>,
  centerY: MotionValue<number>,
  blink: boolean,
) {
  const session: AttentionSession = {
    stopped: false,
    busy: false,
    timer: 0,
    mode: 'static',
    blink,
    lidScale,
    looking,
    centerX,
    centerY,
  }

  const stops = [pointerX, pointerY, centerX, centerY, pointerEngaged].map(value =>
    value.on('change', () => sync(session)),
  )
  sync(session)

  return () => {
    session.stopped = true
    window.clearTimeout(session.timer)
    lidScale.set(LID_OPEN)
    looking.set(0)
    for (const stop of stops) {
      stop()
    }
  }
}
